import { Card, CardContent } from "@/components/ui/card";
import { Eye, MousePointer, Target, DollarSign, Heart, Ticket, CheckCircle2 } from "lucide-react";

/**
 * Shared Metric Card component used by both Dashboard and Analytics pages
 * Ensures consistent styling and display across the application
 */

const metricConfig = {
  impressions: {
    label: "Views",
    sublabel: "Total Impressions",
    icon: Eye,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  clicks: {
    label: "Clicks",
    sublabel: null, // Will be replaced with CTR
    icon: MousePointer,
    iconBg: "bg-orange-500/10",
    iconColor: "text-orange-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  cpdc: {
    label: "Delivered Customers",
    sublabel: "Passengers who visited",
    icon: Target,
    iconBg: "bg-purple-500/10",
    iconColor: "text-purple-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  spend: {
    label: "Total Spend",
    sublabel: "AUD",
    icon: DollarSign,
    iconBg: "bg-green-500/10",
    iconColor: "text-green-500",
    format: (value) => `$${value?.toLocaleString() || "0"}`
  },
  favorites: {
    label: "Favourites",
    sublabel: "Passengers following your venue",
    icon: Heart,
    iconBg: "bg-pink-500/10",
    iconColor: "text-pink-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  saved_offers: {
    label: "Saved Offers",
    sublabel: "Deals saved from in-car displays",
    icon: Ticket,
    iconBg: "bg-yellow-500/10",
    iconColor: "text-yellow-500",
    format: (value) => value?.toLocaleString() || "0"
  },
  redeems: {
    label: "Redeems",
    sublabel: "Deals redeemed in-store",
    icon: CheckCircle2,
    iconBg: "bg-green-500/10",
    iconColor: "text-green-500",
    format: (value) => value?.toLocaleString() || "0"
  }
};

export function MetricCard({ 
  type, 
  value, 
  ctr = null,  // For clicks card
  direct = null, // For cpdc breakdown
  proximity = null, // For cpdc breakdown
  prompted = null, // For cpdc breakdown
  testId = null,
  className = ""
}) {
  const config = metricConfig[type];
  if (!config) return null;
  
  const Icon = config.icon;
  const hasCpdcBreakdown = type === 'cpdc' && (direct !== null || proximity !== null || prompted !== null);
  
  return (
    <Card className={`bg-card border-border ${className}`} data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2.5">
          <div className={`p-2.5 rounded-lg ${config.iconBg}`}>
            <Icon className={`h-6 w-6 ${config.iconColor}`} />
          </div>
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            {config.label}
          </p>
        </div>
        <p className="text-3xl font-bold mt-3">
          {config.format(value)}
        </p>
        {type === 'clicks' && ctr !== null ? (
          <p className="text-xs text-muted-foreground mt-1">
            CTR: {ctr}%
          </p>
        ) : hasCpdcBreakdown ? (
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            <p>Direct: {(direct || 0).toLocaleString()}</p>
            <p>Proximity: {(proximity || 0).toLocaleString()}</p>
            {(prompted || 0) > 0 && <p>Prompted: {(prompted || 0).toLocaleString()}</p>}
          </div>
        ) : config.sublabel ? (
          <p className="text-xs text-muted-foreground mt-1">
            {config.sublabel}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Chart color constants - use these across all charts for consistency
 */
export const CHART_COLORS = {
  impressions: "#3B82F6",  // blue-500 (Views)
  clicks: "#F97316",       // orange-500 (Clicks)
  deals_saved: "#EAB308",  // yellow-500 (Deals Saved)
  cpdc: "#A855F7",         // purple-500 (Delivered Customers)
  spend: "#22C55E"         // green-500 (Total Spend)
};

/**
 * Gradient definitions for area charts
 */
export const getChartGradient = (id, color) => ({
  id,
  color,
  stops: [
    { offset: "5%", opacity: 0.3 },
    { offset: "95%", opacity: 0 }
  ]
});

export default MetricCard;
